import React, { useCallback, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, Modal, ModalBody } from 'reactstrap';
import Select from "react-select";
import { useSelector } from "react-redux";
import { getTables } from "../../../helpers/backend_helper";

interface ShopTable {
    id: number;
    number: string;
    name?: string | null;
    zone?: {
        id?: number | string | null;
        name?: string | null;
        title?: string | null;
    } | null;
    zone_name?: string | null;
    zoneName?: string | null;
}

export interface ChooseServingOptionProps {
    onSelect?: (option: string) => void;
    table?: string | null;
    shopSlug?: string | null;
    onBack?: () => void;
}

const ChooseServingOption: React.FC<ChooseServingOptionProps> = ({
    onSelect,
    table,
    shopSlug,
    onBack,
}) => {
    const shopMenu = useSelector((state: any) => state.ShopMenu?.shopMenu);
    const [modal_small, setmodal_small] = useState(false);
    const [tableNumber, setTableNumber] = useState<string>(table ?? "");
    const [tables, setTables] = useState<ShopTable[]>([]);
    const [manualInput, setManualInput] = useState(false);
    const [isLoadingTables, setIsLoadingTables] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const storeTables = useMemo<ShopTable[]>(() => {
        if (Array.isArray(shopMenu?.shop?.tables)) {
            return shopMenu.shop.tables;
        }
        if (Array.isArray(shopMenu?.tables)) {
            return shopMenu.tables;
        }
        return [];
    }, [shopMenu]);

    const getZoneName = useCallback((tableItem: ShopTable) => {
        const fromRelation = tableItem?.zone?.name ?? tableItem?.zone?.title;
        const fromFlatten = tableItem?.zone_name ?? tableItem?.zoneName;
        return (fromRelation ?? fromFlatten ?? "").toString().trim();
    }, []);

    const getTableNumber = useCallback((tableItem: ShopTable) => {
        const value = tableItem?.number ?? tableItem?.name ?? "";
        return value.toString().trim();
    }, []);

    const buildTableLabel = useCallback((tableItem: ShopTable) => {
        const number = getTableNumber(tableItem);
        const zoneName = getZoneName(tableItem);
        if (zoneName) {
            return `${zoneName} - ${number}`;
        }
        return number;
    }, [getTableNumber, getZoneName]);

    const tableOptions = useMemo(() => {
        return tables.map((tableItem) => ({
            value: tableItem.id?.toString() ?? "",
            label: buildTableLabel(tableItem),
        }));
    }, [tables, buildTableLabel]);

    const selectedOption = useMemo(() => {
        if (!tableNumber) {
            return null;
        }
        return tableOptions.find((option) => option.value === tableNumber) ?? null;
    }, [tableOptions, tableNumber]);

    const normalizeTableId = (value?: string | number | null) => {
        if (value === null || value === undefined) {
            return null;
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) {
            return null;
        }
        return numeric;
    };

    const hasValidTableParam = useMemo(() => {
        if (!table) {
            return false;
        }
        if (!storeTables.length) {
            return false;
        }
        const paramId = normalizeTableId(table);
        if (!paramId) {
            return false;
        }
        return storeTables.some((item: ShopTable) => normalizeTableId(item.id) === paramId);
    }, [storeTables, table]);

    useEffect(() => {
        if (storeTables.length > 0) {
            setTables(storeTables);
        }
    }, [storeTables]);

    useEffect(() => {
        if (!modal_small || tables.length > 0 || isLoadingTables || storeTables.length > 0) {
            return;
        }

        if (!shopSlug) {
            setFetchError("Không xác định được cửa hàng.");
            return;
        }

        let mounted = true;
        setIsLoadingTables(true);
        setFetchError(null);

        getTables({ slug: shopSlug })
            .then((res: any) => {
                if (!mounted) return;
                const data = Array.isArray(res?.data) ? res.data : res;
                if (Array.isArray(data)) {
                    setTables(data as any);
                } else {
                    setTables([]);
                }
            })
            .catch((error: any) => {
                console.error("Failed to load tables", error);
                if (mounted) {
                    setFetchError("Không thể tải danh sách bàn. Vui lòng thử lại.");
                }
            })
            .finally(() => {
                if (mounted) {
                    setIsLoadingTables(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [modal_small, tables.length, isLoadingTables, storeTables.length, shopSlug]);

    function tog_small() {
        setmodal_small(!modal_small);
    }

    return (
        <React.Fragment>
            <Modal
                size="sm"
                isOpen={modal_small}
                toggle={() => {
                    tog_small();
                }}
                centered
            >
                <ModalBody>
                    <h6 className="fs-15">Vui lòng chọn số bàn bạn đang ngồi</h6>
                    <div className="mb-3">
                        {manualInput ? (
                            <input
                                className="form-control"
                                type="number"
                                id="cardholder"
                                placeholder="VD: 68"
                                value={tableNumber}
                                onChange={(e) => setTableNumber(e.target.value)}
                            />
                        ) : (
                            <Select
                                isLoading={isLoadingTables}
                                options={tableOptions}
                                noOptionsMessage={() => fetchError ?? "Không có dữ liệu"}
                                value={selectedOption}
                                onChange={(option: any) => setTableNumber(option?.value || "")}
                                classNamePrefix="react-select"
                                placeholder={isLoadingTables ? "Đang tải bàn..." : "Chọn bàn"}
                            />
                        )}
                    </div>
                    {fetchError && (
                        <div className="alert alert-warning py-2" role="alert">
                            {fetchError}
                        </div>
                    )}
                    <div className="text-end">
                        {manualInput ? (
                            <Button color="link" className="p-0" onClick={() => setManualInput(false)}>
                                Chọn từ danh sách
                            </Button>
                        ) : (
                            <Button color="link" className="p-0" onClick={() => setManualInput(true)}>
                                Nhập tay
                            </Button>
                        )}
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={tog_small}>Hủy</Button>
                    <Button
                        color="primary"
                        disabled={!tableNumber}
                        onClick={() => {
                            if (!tableNumber) {
                                return;
                            }
                            if (onSelect) onSelect(tableNumber);
                            tog_small();
                        }}>
                        Xác nhận
                    </Button>
                </div>
            </Modal>

            <div className="header-area" id="headerArea">
                <div
                    className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <button
                            className="btn btn-link p-0 border-0"
                            type="button"
                            onClick={onBack}
                        >
                            <i className="mdi mdi-arrow-left"></i>
                        </button>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">Chọn nơi nhận món của bạn</h6>
                    </div>

                    <div className="suha-navbar-toggler border-unset ms-2"></div>
                </div>
            </div>
            <div className="page-content-wrapper">
                <div className="container">
                    <div className="checkout-wrapper-area py-3">
                        <div className="choose-payment-method">
                            <div className="row g-2 justify-content-center rtl-flex-d-row-r">
                                <div className="col-6 col-md-5">
                                    <div className="single-payment-method single-payment-method-custom">
                                        <Link
                                            className="credit-card"
                                            to="/#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (hasValidTableParam && table) {
                                                    if (onSelect) {
                                                        onSelect(table);
                                                    }
                                                    return;
                                                }
                                                tog_small();
                                            }}
                                        >
                                            <i className="mdi mdi-table-chair"></i>
                                            <h6>Nhận tại bàn</h6>
                                        </Link>
                                    </div>
                                </div>
                                <div className="col-6 col-md-5">
                                    <div className="single-payment-method single-payment-method-custom">
                                        <Link
                                            className="bank"
                                            to="/#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (onSelect) onSelect('counter');
                                            }}
                                        >
                                            <i className="mdi mdi-desktop-classic"></i>
                                            <h6>Nhận tại quầy</h6>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ChooseServingOption;
